import { useEffect, useRef } from 'react'
import { api } from '@/lib/api'
import { describePlatform } from '@/lib/platform'

/**
 * Tells the server how this client is running.
 *
 * Whether someone finished onboarding is invisible server-side: an installed
 * PWA and a browser tab make identical HTTP requests, and notification
 * permission is only readable from the page. Reporting it is what lets an admin
 * see who never got set up — the person who installed the app but declined
 * notifications is otherwise indistinguishable from one who did neither.
 *
 * Deliberately fire-and-forget and once per mount: this is diagnostics, and it
 * must never delay a page or surface an error to the user.
 */
export function useClientReport(enabled: boolean): void {
  const sent = useRef(false)

  useEffect(() => {
    if (!enabled || sent.current) return
    sent.current = true

    const platform = describePlatform()
    const permission =
      typeof Notification === 'undefined'
        ? ('unsupported' as const)
        : (Notification.permission as 'granted' | 'denied' | 'default')

    void api.account
      .reportClient({
        standalone: platform.standalone,
        platform: platform.platform,
        pushPermission: platform.pushSupported ? permission : 'unsupported',
      })
      .catch(() => {
        // Diagnostics only — a failure here means nothing to the user.
      })
  }, [enabled])
}
